import React from "react";

import { MenuItem, OutlinedInput, Tooltip } from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import SkeletonComponent from "../../Generic/Skeleton";
import { Props as SelectProps } from "../Select";
import { ChipBox, ChipCount, ChipValue, LabelAddAll, LabelRemoveAll, SelectCustom } from "./styles";

type TMenuItem = {
  key: React.Key;
  label?: string;
};

type MenuItems = Array<TMenuItem>;

type SelectMultipleProps = SelectProps & {
  loading?: boolean;
  items: MenuItems;
  selecteds: MenuItems;
  onAddAll: (items: MenuItems) => void;
  onRemoveAll: () => void;
  renderInternalCount?: boolean;
};

type SelectMultipleCountProps = {
  items: SelectMultipleProps["items"];
  selecteds: SelectMultipleProps["selecteds"];
};

const SelectMultiple: React.FC<SelectMultipleProps> = (props) => {
  const {
    loading,
    items,
    selecteds,
    onAddAll,
    onRemoveAll,
    renderInternalCount = true,
    ...selectProps
  } = props;

  const { t: translate } = useTranslation();

  const renderAddAllItems = () => {
    return (
      <MenuItem value={0} onClick={() => onAddAll(items)}>
        <LabelAddAll>
          <AddCircleOutline className="mr-3" />
          {translate("addAll")}
        </LabelAddAll>
      </MenuItem>
    );
  };

  const renderRemoveAllItems = () => {
    return (
      <MenuItem value={-1} onClick={() => onRemoveAll()}>
        <LabelRemoveAll>
          <RemoveCircleOutline className="mr-3" />
          {translate("removeAll")}
        </LabelRemoveAll>
      </MenuItem>
    );
  };

  const renderChipValues = () => {
    return (
      <ChipBox>
        {selecteds?.map((item) => {
          return <ChipValue key={item.key} label={item.label} />;
        })}
      </ChipBox>
    );
  };

  return (
    <>
      <SkeletonComponent isLoading={!!loading}>
        <SelectCustom
          {...selectProps}
          multiple
          renderValue={() => renderChipValues()}
          input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
        >
          {selecteds.length < items.length && renderAddAllItems()}
          {selecteds.length === items.length && renderRemoveAllItems()}
          {items.map((item) => (
            <MenuItem data-cy={item.label} value={item.key} key={item.key}>
              {item.label}
            </MenuItem>
          ))}
        </SelectCustom>
      </SkeletonComponent>
      {renderInternalCount && <SelectMultipleCount items={items} selecteds={selecteds} />}
    </>
  );
};

const SelectMultipleCount: React.FC<SelectMultipleCountProps> = (props) => {
  const { items, selecteds } = props;

  if (!items.length) return null;

  return (
    <Tooltip title={selecteds.length}>
      <ChipCount label={selecteds.length} />
    </Tooltip>
  );
};

export { SelectMultipleCount };
export default SelectMultiple;
